import {
  Fragment,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconPlus, IconZoomOutline } from '@iwoca/icons';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { Button, Input, Spinner } from '@iwoca/orion';
import { clsx } from 'clsx';
import { useFormContext } from 'react-hook-form';

import styles from './CompanyInputUK.module.css';
import { TSicCode } from '../../../../api/apiCompanyQuestion/fetchPostCompanyQuestion';
import { fetchIwocaGetCompanySearch } from '../../../../api/lendingApi/lendingApiQueries';
import { TCompanyDetailsDataUK } from '../CompanyDetailsUK';

type CompanySearchCompany = GetCompanySearchResponse['data'][number];

const getSessionStorageKey = (companyHouseNumber: string) =>
  `sic_codes_for_company_${companyHouseNumber}`;

const setSessionData = (
  companyHouseNumber: GetCompanySearchResponse['data'][number]['name'],
  data: TSicCode,
): void => {
  sessionStorage.setItem(
    getSessionStorageKey(companyHouseNumber),
    JSON.stringify(data),
  );
};

type TProps = {
  placeholder?: string;
  iconPosition?: 'left' | 'right';
  labelAccessory?: ReactNode;
};

const CompanyInputUK = ({
  placeholder = 'Search a company name or number',
  iconPosition = 'right',
  labelAccessory,
}: TProps) => {
  const {
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext<TCompanyDetailsDataUK>();

  // Each time the API is called, we add one to this value. This lets us continue to show
  // the spinner until the last call is resolved
  const [numberOfCallsLoading, setNumberOfCallsLoading] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const [companyName, companyNumber] = watch(['companyName', 'companyNumber']);
  const showDefaultCompany = Boolean(companyName && companyNumber);

  const [selectedCompany, setSelectedCompany] =
    useState<GetCompanySearchResponse['data'][number]>();

  const searchIcon = !selectedCompany ? <IconZoomOutline /> : undefined;

  const [companyOptions, setCompanyOptions] = useState<CompanySearchCompany[]>(
    [],
  );

  useEffect(() => {
    // Search "approve" or "decline" for fake STAGING companies
    const companySearch = async () => {
      setNumberOfCallsLoading((prevNumber) => prevNumber + 1);
      if (searchQuery) {
        try {
          const response = await fetchIwocaGetCompanySearch({
            query: { company_name: searchQuery },
          });

          if (response) {
            setCompanyOptions(response.data);
          }
        } catch {
          setCompanyOptions([]);
        }
      } else {
        setCompanyOptions([]);
        setSelectedCompany(undefined);
      }
      setNumberOfCallsLoading((prevNumber) => prevNumber - 1);
    };

    companySearch();
  }, [searchQuery]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && numberOfCallsLoading > 0) {
      e.preventDefault();
    }
  };

  const handleChange = (company: GetCompanySearchResponse['data'][number]) => {
    if (!company) {
      setSelectedCompany(undefined);
      return;
    }

    setSelectedCompany(company);

    setValue('companyName', company.name, {
      shouldValidate: true,
    });

    setValue('companyNumber', company.number, {
      shouldValidate: true,
    });

    setValue('companyType', company.company_type, {
      shouldValidate: true,
    });

    setValue('startDateYear', company.registration_date.split('-')[0], {
      shouldValidate: true,
    });

    setValue('startDateMonth', company.registration_date.split('-')[1], {
      shouldValidate: true,
    });

    setSessionData(company.number, company.sic_codes || []);

    tracking.ampli.companyNameSearched();
  };

  const buildDefaultValue = () =>
    getValues('companyName') && getValues('companyNumber')
      ? `${getValues('companyName')} | ${getValues('companyNumber')}`
      : '';

  const renderOptions = useCallback(() => {
    if (numberOfCallsLoading > 0) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      );
    }

    return (
      <>
        {companyOptions && companyOptions.length > 0 ? (
          companyOptions.map((company, index) => (
            <ComboboxOption
              className={({ focus }) =>
                clsx(
                  styles.option,
                  {
                    [styles.focus]: focus,
                  },
                  'dd-privacy-mask',
                )
              }
              key={index}
              value={company}
            >
              {company.name} | {company.number}
            </ComboboxOption>
          ))
        ) : (
          <p className={styles.noResults}>No results</p>
        )}
      </>
    );
  }, [numberOfCallsLoading]);

  const resetSelectedCompany = () => {
    setSearchQuery('');
    setSelectedCompany(undefined);

    setValue('companyName', '');
    setValue('companyNumber', '');
    sessionStorage.clear();
  };

  const label = labelAccessory ? (
    <span className={styles.labelRow}>
      Limited company
      {labelAccessory}
    </span>
  ) : (
    'Limited company'
  );

  return (
    <Combobox onChange={handleChange}>
      <div className={styles.companiesSearch}>
        {selectedCompany || showDefaultCompany ? (
          <>
            <Input
              className={styles.companyInput}
              label={label}
              value={buildDefaultValue()}
              description="Your company must be registered with Companies House to proceed."
              readOnly
              error={
                (errors?.companyName?.message && errors.companyName.message) ||
                (errors?.companyNumber?.message && errors.companyNumber.message)
              }
            />
            <Button
              className={styles.clearButton}
              type="reset"
              variant="tertiary"
              size="small"
              data-testid="clear-button-company"
              onClick={resetSelectedCompany}
            >
              <IconPlus
                className={styles.clearButtonIcon}
                height="24"
                width="24"
              />
            </Button>
          </>
        ) : (
          <ComboboxInput
            onChange={(event) => {
              setSearchQuery(event.target.value);
            }}
            as={Fragment}
            displayValue={(company: CompanySearchCompany | null) =>
              company ? `${company.name} | ${company.number}` : ''
            }
          >
            <Input
              iconLeft={iconPosition === 'left' ? searchIcon : undefined}
              iconRight={iconPosition === 'right' ? searchIcon : undefined}
              label={label}
              description="Your company must be registered with Companies House to proceed."
              placeholder={placeholder}
              className={styles.companyInput}
              onKeyDown={handleKeyDown}
              error={
                (errors?.companyName?.message && errors.companyName.message) ||
                (errors?.companyNumber?.message && errors.companyNumber.message)
              }
            />
          </ComboboxInput>
        )}
        {searchQuery && (
          <ComboboxOptions className={styles.options} modal={false}>
            {renderOptions()}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
};

export default CompanyInputUK;
